import { Activity, BarChart3, Radar } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";

// LandingPage is the public marketing shell shown to logged-out users in
// multi-tenant mode. It makes NO gated API calls and has no data-fetching
// hooks — App decides to render it from /api/me (auth_enabled &&
// !authenticated) before the dashboard ever mounts, so the gated
// /api/settings call never fires and the global 401 redirect in
// request() never triggers. The CTAs are plain anchors to /auth/login;
// the Go server 302s on to the central auth service from there.
const features = [
  {
    icon: BarChart3,
    title: "Ranked setups",
    body: "Support/resistance, risk:reward, and a quality-first overall score surface the best swing-trade setups in your watchlist — not just the biggest movers.",
  },
  {
    icon: Activity,
    title: "Live tracker",
    body: "Intraday 1-minute snapshots stream each name's live price against its baseline, flagging arming, triggers, and stop invalidations as the session moves.",
  },
  {
    icon: Radar,
    title: "Scanner",
    body: "A background crawler sweeps the ~6k-name NASDAQ universe, applying the same liquidity, float, and quality gates so fresh setups bubble up automatically.",
  },
];

export function LandingPage({ version }: { version: string }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-4">
          <h1 className="text-2xl font-bold tracking-tight">taco</h1>
          <div className="flex items-center gap-2">
            <Button asChild>
              <a href="/auth/login">Log in / Sign up</a>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-[1100px] flex-1 flex-col px-6">
        <section className="flex flex-col items-center py-20 text-center md:py-28">
          <h2 className="max-w-2xl text-4xl font-bold tracking-tight md:text-5xl">
            Swing-trade analysis, ranked.
          </h2>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">
            Support/resistance, risk, and ranking for your watchlist — with a
            live intraday tracker and a market-wide scanner.
          </p>
          <div className="mt-8">
            <Button asChild size="lg">
              <a href="/auth/login">Get started</a>
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 pb-20 md:grid-cols-3">
          {features.map(({ icon: Icon, title, body }) => (
            <Card key={title}>
              <CardHeader>
                <Icon className="size-6 text-primary" aria-hidden="true" />
                <CardTitle className="mt-2">{title}</CardTitle>
                <CardDescription>{body}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </section>
      </main>

      {version && (
        <div
          className="pointer-events-none fixed bottom-2 left-3 select-none font-mono text-[10px] text-muted-foreground/40"
          title="taco build"
        >
          {version}
        </div>
      )}
    </div>
  );
}

export default LandingPage;
